import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Stack,
  Chip,
  Paper,
} from "@mui/material";
import { useAuthStore } from "../store/authStore";
import { IMerchants } from "../utils/types";
import MerchRateCard from "./MerchRateCard";
import { useMethods } from "../hooks/useMethods";
import { translate } from "../utils/currency";
import { useTheme } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import SouthWestRounded from "@mui/icons-material/SouthWestRounded";
import NorthEastRounded from "@mui/icons-material/NorthEastRounded";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";

const MerchantCard: React.FC<IMerchants> = ({
  rates,
  merchantID,
  merchantName,
  dialogRef,
  fetchData,
  display,
  setDisplay,
}) => {
  const { curr } = useAuthStore();
  const { currency, methods } = useMethods();
  const theme = useTheme();

  const merchantRates: {
    operation_type: string;
    payment_method: string;
    currency: string;
    rate: number;
    minsum: number;
    maxsum: number;
  }[] =
    rates[merchantID]?.filter(
      (el) => el.currency.toLowerCase() === curr.toLowerCase(),
    ) || [];

  const sections = [
    {
      type: "deposit",
      title: "Пополнение",
      color: theme.palette.primary.main,
      background: "rgba(99, 91, 255, 0.10)",
      icon: SouthWestRounded,
    },
    {
      type: "payout",
      title: "Выплата",
      color: theme.palette.success.main,
      background: "rgba(46, 174, 128, 0.10)",
      icon: NorthEastRounded,
    },
    {
      type: "cashin",
      title: "Cash in",
      color: theme.palette.warning.main,
      background: "rgba(245, 166, 35, 0.10)",
      icon: PaymentsOutlined,
    },
    {
      type: "withdrawal",
      title: "Вывод",
      color: theme.palette.error.main,
      background: "rgba(239, 83, 80, 0.10)",
      icon: NorthEastRounded,
    },
  ];

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: "20px",
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        backgroundImage: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: 2.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          mb={2.5}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 750, lineHeight: 1.25 }}>
              {merchantName}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1} mt={0.75}>
              <Chip
                label={`ID ${merchantID}`}
                size="small"
                sx={{ height: 23, fontSize: "11px" }}
              />
              <Typography variant="caption" color="text.secondary">
                {merchantRates.length} ставок · {curr.toUpperCase()}
              </Typography>
            </Stack>
          </Box>
          <Button
            onClick={() => {
              if (display === merchantName) {
                setDisplay("");
              } else {
                setDisplay(merchantName);
              }
            }}
            variant={display === merchantName ? "contained" : "outlined"}
            size="small"
            startIcon={<TuneRounded />}
            disabled={merchantRates.length === 0}
            sx={{ flexShrink: 0 }}
          >
            {display === merchantName ? "Готово" : "Изменить ставки"}
          </Button>
        </Stack>
        {display !== merchantName ? (
          <>
            {merchantRates.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "12px",
                }}
              >
                {sections.map((section) => {
                  const sectionRates = merchantRates.filter(
                    (rate) => rate.operation_type === section.type,
                  );

                  if (sectionRates.length === 0) return null;

                  const OperationIcon = section.icon;

                  return (
                    <Paper
                      key={section.type}
                      variant="outlined"
                      sx={{
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "none",
                        backgroundImage: "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ p: "13px 14px", borderBottom: "1px solid", borderColor: "divider" }}
                      >
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "10px",
                              display: "grid",
                              placeItems: "center",
                              color: section.color,
                              backgroundColor: section.background,
                            }}
                          >
                            <OperationIcon sx={{ fontSize: 19 }} />
                          </Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {section.title}
                          </Typography>
                        </Stack>
                        <Chip
                          label={sectionRates.length}
                          size="small"
                          sx={{ height: 23, minWidth: 30, fontSize: "11px" }}
                        />
                      </Stack>

                      <Stack sx={{ p: "6px" }}>
                        {sectionRates.map((item, index) => (
                          <Box
                            key={`${item.payment_method}-${item.minsum}-${item.maxsum}-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                              p: "10px",
                              borderRadius: "11px",
                              "&:hover": { backgroundColor: "action.hover" },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={650} noWrap>
                                {translate(item.payment_method)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.minsum.toLocaleString("ru-RU")}–
                                {item.maxsum.toLocaleString("ru-RU")} {item.currency.toUpperCase()}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                px: "10px",
                                py: "5px",
                                borderRadius: "9px",
                                color: section.color,
                                backgroundColor: section.background,
                                fontWeight: 800,
                                fontSize: "13px",
                                flexShrink: 0,
                              }}
                            >
                              {item.rate}%
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  mt: 2,
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                  textAlign: "center",
                }}
              >
                Ставки по выбранной валюте отсутствуют
              </Alert>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                merchantRates.length === 1
                  ? "minmax(300px, 400px)"
                  : "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "15px",
              width: "100%",
              alignItems: "start",
            }}
          >
            {merchantRates.map((el, index) => (
              <MerchRateCard
                key={`${el.operation_type}-${el.payment_method}-${el.minsum}-${el.maxsum}-${index}`}
                methods={methods}
                currencies={currency}
                data={el}
                merchantID={merchantID}
                dialogRef={dialogRef}
                fetchData={fetchData}
              />
            ))}
          </Box>
        )}
        <Button
          onClick={() => dialogRef.current?.openDialog()}
          fullWidth
          variant="outlined"
          startIcon={<AddRounded />}
          sx={{ mt: 2, minHeight: 42 }}
        >
          Добавить ставку
        </Button>
      </CardContent>
    </Card>
  );
};

export default MerchantCard;
